/**
 * PenaltyControlModal - Display detailed compliance guidance for penalty controls
 * 
 * Shows regulatory article, requirement details, remediation steps, 
 * needed documents/processes, and AI-generated compliance advice.
 */
import React, { useState, useEffect } from 'react';
import { GRCControl, Framework } from '../../types/grc';
import { aiService } from '../../services/aiService';
import { 
  X, Gavel, AlertTriangle, FileText, CheckCircle2, 
  ClipboardList, Shield, Users, Building2, Sparkles,
  Clock, Loader2, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

interface PenaltyControlModalProps {
  control: GRCControl;
  framework: Framework;
  onClose: () => void;
}

// Penalty compliance guidance by framework code
const COMPLIANCE_GUIDANCE: Record<string, Record<string, {
  regulatoryBasis: string;
  penaltyAmount: string;
  deadline?: string;
  requiredDocuments: string[];
  implementationSteps: string[];
  responsibleRoles: string[];
  evidence: string[];
}>> = {
  'ZIB': {
    'default': {
      regulatoryBasis: 'Zakon o informacionoj bezbednosti ("Sl. glasnik RS", br. 91/2025)',
      penaltyAmount: 'do 2.000.000 RSD za pravno lice, do 150.000 RSD za odgovorno lice',
      deadline: '6 meseci od stupanja na snagu',
      requiredDocuments: [
        'Politika informacione bezbednosti',
        'Plan upravljanja rizicima',
        'Procedure za prijavu incidenata',
        'Evidencija o implementiranim merama',
        'Zapisnici o obuci zaposlenih'
      ],
      implementationSteps: [
        '1. Formirati tim za informacionu bezbednost',
        '2. Sprovesti analizu rizika IKT sistema',
        '3. Izraditi i usvojiti politiku informacione bezbednosti',
        '4. Implementirati tehničke mere zaštite',
        '5. Uspostaviti procedure za upravljanje incidentima',
        '6. Obučiti zaposlene o procedurama',
        '7. Prijaviti se nadležnom organu (ako je primenjivo)'
      ],
      responsibleRoles: [
        'Rukovodilac informacione bezbednosti',
        'IT menadžer',
        'Pravna služba',
        'Uprava'
      ],
      evidence: [
        'Usvojena politika sa potpisom uprave',
        'Izveštaj o analizi rizika',
        'Tehnička konfiguracija sistema',
        'Evidencija o obukama',
        'Logovi sistema za detekciju'
      ]
    }
  },
  'GDPR': {
    'default': {
      regulatoryBasis: 'Opšta uredba o zaštiti podataka (EU) 2016/679',
      penaltyAmount: 'do 20.000.000 EUR ili 4% godišnjeg prometa',
      requiredDocuments: [
        'Evidencija aktivnosti obrade',
        'Procena uticaja na zaštitu podataka (DPIA)',
        'Politika privatnosti',
        'Procedure za ostvarivanje prava lica',
        'Ugovori o obradi sa obrađivačima'
      ],
      implementationSteps: [
        '1. Mapirati sve aktivnosti obrade ličnih podataka',
        '2. Odrediti pravni osnov za svaku obradu',
        '3. Ažurirati politike privatnosti',
        '4. Uspostaviti procedure za zahteve lica',
        '5. Sprovoditi DPIA za rizične obrade',
        '6. Implementirati mere bezbednosti',
        '7. Imenovati DPO (ako je obavezno)'
      ],
      responsibleRoles: [
        'Data Protection Officer (DPO)',
        'Legal/Compliance tim',
        'IT bezbednost',
        'HR (za podatke zaposlenih)'
      ],
      evidence: [
        'Ažurna evidencija aktivnosti obrade',
        'Potpisane politike i procedure',
        'DPIA izveštaji',
        'Logovi pristupa podacima',
        'Dokazi o obuci zaposlenih'
      ]
    }
  },
  'NIS2': {
    'default': {
      regulatoryBasis: 'Direktiva (EU) 2022/2555 o mrežnoj i informacionoj bezbednosti',
      penaltyAmount: 'do 10.000.000 EUR ili 2% godišnjeg prometa',
      deadline: '17. oktobar 2024. (transpozicija)',
      requiredDocuments: [
        'Politika upravljanja rizicima',
        'Plan kontinuiteta poslovanja',
        'Procedure za upravljanje incidentima',
        'Politika bezbednosti lanca snabdevanja',
        'Program obuke za sajber bezbednost'
      ],
      implementationSteps: [
        '1. Utvrditi da li ste esencijalni ili važni entitet',
        '2. Uspostaviti okvir za upravljanje rizicima',
        '3. Implementirati minimalne mere bezbednosti',
        '4. Uspostaviti tim za odgovor na incidente',
        '5. Proveriti bezbednost lanca snabdevanja',
        '6. Registrovati se kod nadležnog organa',
        '7. Redovno testirati mere bezbednosti'
      ],
      responsibleRoles: [
        'CISO',
        'IT operacije',
        'Uprava (lična odgovornost)',
        'Team za incident response'
      ],
      evidence: [
        'Dokumentovane politike i procedure',
        'Izveštaji o testiranju bezbednosti',
        'Evidencija o incidentima',
        'Ugovori sa dobavljačima',
        'Sertifikati i potvrde o usklađenosti'
      ]
    }
  }
};

export const PenaltyControlModal: React.FC<PenaltyControlModalProps> = ({
  control,
  framework,
  onClose
}) => {
  const { t } = useI18n();
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  // Get compliance guidance for this framework
  const guidance = COMPLIANCE_GUIDANCE[framework.code]?.[control.control_code] 
    || COMPLIANCE_GUIDANCE[framework.code]?.['default']
    || COMPLIANCE_GUIDANCE['ZIB']?.['default']; // Fallback

  // Get AI analysis for this specific control
  const loadAiAnalysis = async () => {
    setLoadingAi(true);
    try {
      // Convert GRCControl to SoAControl format for aiService
      const soaControl = {
        id: String(control.id),
        controlId: control.control_code || control.article_code || String(control.id),
        name: control.title,
        description: control.description || '',
        category: 'general',
        applicable: control.applicable === 1,
        justification: '',
        implementationStatus: control.status,
        implementationDate: '',
        owner: control.owner || '',
        evidence: [],
        notes: control.guidance || ''
      };
      const result = await aiService.getImplementationGuidance(soaControl as any, framework.name, {
        code: framework.code,
        type: framework.framework_type,
      });
      const analysis = [
        result.summary || '',
        result.implementationSteps?.length > 0 ? `\n\nKoraci:\n${result.implementationSteps.join('\n')}` : '',
        result.requiredDocuments?.length > 0 ? `\n\nDokumenti:\n${result.requiredDocuments.join('\n')}` : '',
        result.suggestedOwnerRole ? `\n\nPreporučeni vlasnik: ${result.suggestedOwnerRole}` : ''
      ].join('');
      setAiAnalysis(analysis || 'Analiza je uspešno generisana.');
    } catch (e) {
      setAiAnalysis('AI analiza trenutno nije dostupna. Proverite podešavanja OpenAI API ključa.');
    }
    setLoadingAi(false);
  };

  useEffect(() => {
    loadAiAnalysis();
  }, [control.id]);

  // Status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'partial': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'non_compliant': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'compliant': return 'Usklađeno';
      case 'partial': return 'Delimično';
      case 'non_compliant': return 'Neusklađeno';
      case 'not_assessed': return 'Nije procenjeno';
      default: return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Gavel className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm opacity-80">
                    {control.control_code || control.article_code}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusColor(control.status)}`}>
                    {statusLabel(control.status)}
                  </span>
                </div>
                <h2 className="text-lg font-bold">{control.title}</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6 space-y-6">
          {/* Control Description */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <p className="text-gray-700 dark:text-gray-300">{control.description}</p>
            
            {control.guidance && (
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">{control.guidance}</p>
                </div>
              </div>
            )}
          </div>

          {/* Regulatory Basis */}
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
              <FileText className="w-4 h-4 text-indigo-500" />
              Pravni osnov
            </h3>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
              <p className="font-medium text-indigo-900 dark:text-indigo-200">{guidance?.regulatoryBasis}</p>
              <div className="flex flex-wrap gap-4 mt-3 text-sm">
                <div>
                  <span className="text-indigo-600 dark:text-indigo-400">Maksimalna kazna: </span>
                  <span className="font-bold text-red-600 dark:text-red-400">{guidance?.penaltyAmount}</span>
                </div>
                {guidance?.deadline && (
                  <div>
                    <span className="text-indigo-600 dark:text-indigo-400">Rok: </span>
                    <span className="font-medium">{guidance.deadline}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Implementation Steps */}
          <div>
            <button 
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                <ClipboardList className="w-4 h-4 text-green-500" />
                Koraci za usklađivanje
              </h3>
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showDetails && guidance?.implementationSteps && (
              <div className="mt-3 space-y-2">
                {guidance.implementationSteps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{step}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Required Documents */}
          {guidance?.requiredDocuments && (
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
                <FileText className="w-4 h-4 text-blue-500" />
                Potrebna dokumentacija
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {guidance.requiredDocuments.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                    <FileText className="w-3 h-3 text-blue-500" />
                    {doc}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Responsible Roles */}
          {guidance?.responsibleRoles && (
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
                <Users className="w-4 h-4 text-purple-500" />
                Odgovorne uloge
              </h3>
              <div className="flex flex-wrap gap-2">
                {guidance.responsibleRoles.map((role, idx) => (
                  <span key={idx} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm">
                    {role}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Required Evidence */}
          {guidance?.evidence && (
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
                <Shield className="w-4 h-4 text-amber-500" />
                Potrebni dokazi za reviziju
              </h3>
              <div className="space-y-2">
                {guidance.evidence.map((ev, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-3 h-3 text-amber-500" />
                    {ev}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Analysis */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              AI preporuka za vašu organizaciju
            </h3>
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
              {loadingAi ? (
                <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Generisanje AI analize...</span>
                </div>
              ) : (
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {aiAnalysis}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
          >
            Zatvori
          </button>
        </div>
      </div>
    </div>
  );
};

export default PenaltyControlModal;
